import { produce } from 'immer';
import {
  createContext,
  useReducer,
  useContext,
  useEffect,
  type ReactNode,
} from 'react';
import { useLoaderData } from 'react-router-dom';

import { useAuthStatePageReload } from 'src/hooks/useAuthPageReload/useAuthPageReload';
import type { Comment, Thread } from 'src/mapper/threadMapper';
import type { ThreadDetailsData } from 'src/pages/CommunityThreadDetails';

interface ThreadDetailState {
  thread: Thread;
  hasNextPage: boolean;
  error: string | null;
  totalCommentCount: number;
  comments: Comment[];
  isLoadingComments: boolean;
  commentText: string;
  showCommentTextArea: boolean;
  discardState: boolean;
}

type ThreadDetailAction =
  | {
      type: 'RELOAD_THREAD';
      payload: { thread: Thread };
    }
  | {
      type: 'UPDATE_THREAD_LIKE';
      payload: { liked: boolean; likeCountLabel: string };
    }
  | { type: 'SET_ERROR'; payload: { error: string } }
  | { type: 'DISMISS_ERROR' }
  | { type: 'ADD_MORE_COMMENT'; payload: { comments: Comment[] } }
  | { type: 'CREATE_COMMENT'; payload: { comment: Comment } }
  | {
      type: 'UPDATE_COMMENT_TEXT';
      payload: { comment: string };
    }
  | { type: 'DELETE_COMMENT'; payload: { commentId: string } }
  | {
      type: 'UPDATE_COMMENT_LIKE_STATUS';
      payload: { commentId: string; countLabel: string; isLike: boolean };
    }
  | { type: 'SHOW_COMMENT_TEXT_AREA' }
  | { type: 'LOADING_COMMENT' }
  | { type: 'COMPLETE_LOADING_COMMENT' }
  | { type: 'SET_COMMENT_TEXT_AREA_FOCUS'; payload: { isFocus: boolean } }
  | { type: 'SET_DISCARD_STATE'; payload: { discardState: boolean } }
  | { type: 'RESET_COMMENT' };

interface ThreadDetailContextType {
  threadDetailState: ThreadDetailState;
  threadDetailDispatch: React.Dispatch<ThreadDetailAction>;
}

const ThreadDetailContext = createContext<ThreadDetailContextType | undefined>(
  undefined,
);

const threadReducer = (
  state: ThreadDetailState,
  action: ThreadDetailAction,
): ThreadDetailState => {
  switch (action.type) {
    case 'RELOAD_THREAD':
      const thread = action.payload.thread;
      return {
        thread,
        hasNextPage:
          thread.exactSocialData.commentCount > thread.comments.length,
        error: null,
        totalCommentCount: thread.exactSocialData.commentCount,
        comments: thread.comments,
        isLoadingComments: false,
        commentText: '',
        showCommentTextArea: false,
        discardState: false,
      };
    case 'UPDATE_THREAD_LIKE':
      return produce(state, (draft) => {
        draft.thread.liked = action.payload.liked;
        draft.thread.exactSocialData.likeCountLabel =
          action.payload.likeCountLabel;
      });
    case 'ADD_MORE_COMMENT':
      return produce(state, (draft) => {
        draft.comments = [...draft.comments, ...action.payload.comments];
        draft.isLoadingComments = false;
        draft.hasNextPage =
          draft.thread.exactSocialData.commentCount > draft.comments.length;
      });
    case 'CREATE_COMMENT':
      return produce(state, (draft) => {
        draft.comments = [action.payload.comment, ...draft.comments];
        draft.totalCommentCount += 1;
        draft.commentText = '';
        draft.showCommentTextArea = false;
      });
    case 'UPDATE_COMMENT_TEXT':
      return produce(state, (draft) => {
        draft.commentText = action.payload.comment;
      });
    case 'DELETE_COMMENT':
      return produce(state, (draft) => {
        draft.comments = draft.comments.filter(
          ({ id }) => id !== action.payload.commentId,
        );
        draft.totalCommentCount -= 1;
      });
    case 'UPDATE_COMMENT_LIKE_STATUS':
      return produce(state, (draft) => {
        const comment = draft.comments.find(
          ({ id }) => id === action.payload.commentId,
        );
        if (comment) {
          comment.liked = action.payload.isLike;
          comment.exactSocialData.likeCountLabel = action.payload.countLabel;
        }
      });
    case 'SHOW_COMMENT_TEXT_AREA':
      return produce(state, (draft) => {
        draft.showCommentTextArea = true;
      });
    case 'SET_COMMENT_TEXT_AREA_FOCUS':
      return produce(state, (draft) => {
        draft.showCommentTextArea = action.payload.isFocus;
      });
    case 'SET_ERROR':
      return produce(state, (draft) => {
        draft.error = action.payload.error;
        draft.isLoadingComments = false;
      });
    case 'DISMISS_ERROR':
      return produce(state, (draft) => {
        draft.error = null;
      });
    case 'LOADING_COMMENT':
      return produce(state, (draft) => {
        draft.isLoadingComments = true;
      });
    case 'COMPLETE_LOADING_COMMENT':
      return produce(state, (draft) => {
        draft.isLoadingComments = false;
      });
    case 'SET_DISCARD_STATE':
      return produce(state, (draft) => {
        draft.discardState = action.payload.discardState;
      });
    case 'RESET_COMMENT':
      return produce(state, (draft) => {
        draft.discardState = false;
        draft.showCommentTextArea = false;
        draft.commentText = '';
      });
    default:
      throw new Error('pageReducer Unknown action type');
  }
};

interface ThreadDetailProviderProps {
  children: ReactNode;
  thread: Thread;
}

const useRefreshThread = (
  threadDetailDispatch: React.Dispatch<ThreadDetailAction>,
  thread: Thread,
) => {
  useEffect(() => {
    threadDetailDispatch({ type: 'RELOAD_THREAD', payload: { thread } });
  }, [thread, threadDetailDispatch]);
};

export const ThreadDetailProvider: React.FC<ThreadDetailProviderProps> = ({
  children,
  thread,
}) => {
  const { init, isAuthenticated } = useLoaderData() as ThreadDetailsData;
  const [threadDetailState, threadDetailDispatch] = useReducer(threadReducer, {
    thread,
    hasNextPage: thread.exactSocialData.commentCount > thread.comments.length,
    error: null,
    totalCommentCount: thread.totalComments,
    comments: thread.comments,
    isLoadingComments: false,
    commentText: '',
    showCommentTextArea: false,
    discardState: false,
  });

  useAuthStatePageReload(init, isAuthenticated);
  useRefreshThread(threadDetailDispatch, thread);

  return (
    <ThreadDetailContext.Provider
      value={{
        threadDetailState,
        threadDetailDispatch,
      }}
    >
      {children}
    </ThreadDetailContext.Provider>
  );
};

export const useThreadDetail = (): ThreadDetailContextType => {
  const context = useContext(ThreadDetailContext);
  if (!context) {
    throw new Error(
      'useThreadDetail must be used within a ThreadDetailßProvider',
    );
  }
  return context;
};

export default ThreadDetailProvider;
